// app/javascript/pages/Student/Leaderboard/Index.tsx
import { router } from '@inertiajs/react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Trophy, Medal, User, TrendingUp } from 'lucide-react'

interface Entry {
  rank: number
  name: string
  score: number
  user_id: number
  tests_taken?: number
  accuracy?: number
  best_percentile?: number
  best_percentage?: number
}

interface UserPosition {
  rank: number
  score: number
  total: number
  best_percentage?: number
  best_percentile?: number
}

interface Props {
  exams: Array<{ id: number; name: string; code: string }>
  selected_exam_id: number | null
  score_display_mode?: 'raw_score' | 'percentage' | 'percentile' | 'nta_score'
  entries: Entry[]
  user_position: UserPosition | null
  total: number
}

function formatLeaderboardScore(score: number, mode: string, bestPercentage?: number, bestPercentile?: number) {
  if (mode === 'percentage') {
    if (bestPercentage != null) return `${bestPercentage.toFixed(1)}%`
    return `${score}%`
  }
  if (mode === 'percentile' || mode === 'nta_score') {
    return bestPercentile != null ? `${bestPercentile.toFixed(1)}%ile` : `${score} pts`
  }
  return `${score} pts`
}

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="h-4 sm:h-5 w-4 sm:w-5 text-tertiary" />
  if (rank === 2) return <Medal className="h-4 sm:h-5 w-4 sm:w-5 text-muted-foreground" />
  if (rank === 3) return <Medal className="h-4 sm:h-5 w-4 sm:w-5 text-secondary" />
  return <span className="text-xs font-bold text-muted-foreground w-5 text-center font-mono">{rank}</span>
}

export default function LeaderboardIndex({
  exams = [],
  selected_exam_id,
  score_display_mode = 'raw_score',
  entries = [],
  user_position,
  total = 0
}: Props) {
  const handleExamChange = (examId: number) => {
    router.get('/student/leaderboard', { exam_id: examId }, { preserveState: true })
  }
  const selectedExam = exams.find(e => e.id === selected_exam_id) || exams[0]
  const top3 = entries.slice(0, 3)

  const scoreMetricLabel = score_display_mode === 'percentage'
    ? 'Best Percentage'
    : (score_display_mode === 'percentile' || score_display_mode === 'nta_score' ? 'Best Percentile' : 'Best Score')

  return (
    <StudentShell>
      {/* ─── MOBILE VIEW ONLY (< lg) ─── */}
      <div className="block lg:hidden space-y-4 pb-6">
        {/* Hero Header */}
        <div className="space-y-1 pt-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-[10px] font-bold uppercase tracking-widest">
              <Trophy className="h-3 w-3" />
              Nationwide Rankings
            </span>
            {selectedExam?.name && (
              <span className="text-[10px] font-bold bg-muted/60 text-muted-foreground border border-border/70 px-2 py-0.5 rounded-full">
                {selectedExam.name}
              </span>
            )}
          </div>
          <h1 className="text-xl font-extrabold text-foreground tracking-tight">Leaderboard & Standings</h1>
          <p className="text-xs text-muted-foreground">Compare performance, percentile ranks, and top scores across candidates.</p>
        </div>

        {/* Exam Selection Tabs (if multiple exams) */}
        {exams.length > 1 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
            {exams.map(e => (
              <button
                key={e.id}
                type="button"
                onClick={() => handleExamChange(e.id)}
                className={`flex-none px-3 py-1.5 rounded-full text-xs font-semibold border transition-all whitespace-nowrap ${
                  e.id === selectedExam?.id
                    ? 'bg-primary text-primary-fg border-primary font-bold shadow-xs'
                    : 'bg-card text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
                }`}
              >
                {e.name}
              </button>
            ))}
          </div>
        )}

        {/* Quick 3-Metric Stats Strip */}
        <div className="grid grid-cols-3 gap-2">
          <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-primary/20 bg-primary/5 text-center font-bold">
            <span className="text-lg sm:text-xl font-black text-primary">
              {user_position ? `#${user_position.rank}` : '—'}
            </span>
            <span className="text-[10px] font-semibold text-primary uppercase tracking-wider mt-0.5">Your Rank</span>
          </div>

          <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-secondary/20 bg-secondary/5 text-center font-bold">
            <span className="text-lg sm:text-xl font-black text-secondary">
              {user_position ? formatLeaderboardScore(user_position.score, score_display_mode, user_position.best_percentage, user_position.best_percentile) : '—'}
            </span>
            <span className="text-[10px] font-semibold text-secondary uppercase tracking-wider mt-0.5">{scoreMetricLabel}</span>
          </div>

          <div className="flex flex-col items-center justify-center py-2.5 px-1 rounded-2xl border border-border bg-muted/40 text-center font-bold">
            <span className="text-lg sm:text-xl font-black text-foreground">{total}</span>
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">Aspirants</span>
          </div>
        </div>

        {/* User Standing Card (Mobile Optimized — No truncation) */}
        {user_position ? (
          <div className="p-3.5 rounded-2xl border border-primary/25 bg-primary/5 shadow-2xs flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary text-primary-fg flex items-center justify-center font-extrabold text-sm shadow-2xs shrink-0">
                #{user_position.rank}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Your Standing</p>
                <p className="text-xs font-bold text-foreground truncate">
                  Rank #{user_position.rank} <span className="font-normal text-muted-foreground">of {user_position.total} students</span>
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">{scoreMetricLabel}</span>
              <span className="text-sm font-extrabold text-primary">
                {formatLeaderboardScore(user_position.score, score_display_mode, user_position.best_percentage, user_position.best_percentile)}
              </span>
            </div>
          </div>
        ) : (
          <div className="p-3.5 rounded-2xl border border-dashed border-border bg-card/60 text-center text-xs text-muted-foreground flex items-center justify-center gap-2 font-medium shadow-2xs">
            <TrendingUp className="h-4 w-4 text-primary shrink-0" />
            <span>
              Complete a test in <strong className="text-foreground font-bold">{selectedExam?.name}</strong> to enter the leaderboard!
            </span>
          </div>
        )}

        {/* Top 3 Podium Spotlight (Mobile Layout) */}
        {top3.length >= 2 ? (
          <div className="p-3.5 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Trophy className="h-3.5 w-3.5 text-tertiary" /> Top Performers
              </span>
              <span className="text-[10px] text-muted-foreground font-semibold truncate max-w-[150px]">{selectedExam?.name}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 items-end pt-2">
              {/* 2nd Place */}
              {top3[1] && (
                <div className="flex flex-col items-center text-center p-2 rounded-xl border border-border bg-muted/30">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground mb-1">
                    <Medal className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <span className="text-[9px] font-extrabold text-muted-foreground uppercase">2nd</span>
                  <span className="text-[11px] font-bold text-foreground truncate w-full mt-0.5">{top3[1].name}</span>
                  <span className="text-xs font-extrabold text-foreground mt-0.5">
                    {formatLeaderboardScore(top3[1].score, score_display_mode, top3[1].best_percentage, top3[1].best_percentile)}
                  </span>
                </div>
              )}
              {/* 1st Place */}
              {top3[0] && (
                <div className="flex flex-col items-center text-center p-2.5 rounded-xl border border-tertiary/40 bg-tertiary/10 -mt-2 shadow-2xs">
                  <div className="w-9 h-9 rounded-full bg-tertiary/20 flex items-center justify-center text-tertiary mb-1">
                    <Trophy className="h-4.5 w-4.5 text-tertiary" />
                  </div>
                  <span className="text-[9px] font-extrabold text-tertiary uppercase">1st Place</span>
                  <span className="text-xs font-extrabold text-foreground truncate w-full mt-0.5">{top3[0].name}</span>
                  <span className="text-sm font-extrabold text-foreground mt-0.5">
                    {formatLeaderboardScore(top3[0].score, score_display_mode, top3[0].best_percentage, top3[0].best_percentile)}
                  </span>
                </div>
              )}
              {/* 3rd Place */}
              {top3[2] ? (
                <div className="flex flex-col items-center text-center p-2 rounded-xl border border-secondary/30 bg-secondary/5">
                  <div className="w-8 h-8 rounded-full bg-secondary/15 flex items-center justify-center text-secondary mb-1">
                    <Medal className="h-4 w-4 text-secondary" />
                  </div>
                  <span className="text-[9px] font-extrabold text-secondary uppercase">3rd</span>
                  <span className="text-[11px] font-bold text-foreground truncate w-full mt-0.5">{top3[2].name}</span>
                  <span className="text-xs font-extrabold text-foreground mt-0.5">
                    {formatLeaderboardScore(top3[2].score, score_display_mode, top3[2].best_percentage, top3[2].best_percentile)}
                  </span>
                </div>
              ) : (
                <div />
              )}
            </div>
          </div>
        ) : top3.length === 1 ? (
          <div className="p-3.5 rounded-2xl border border-tertiary/30 bg-tertiary/5 flex items-center justify-between shadow-2xs">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-tertiary/20 text-tertiary flex items-center justify-center shrink-0">
                <Trophy className="h-5 w-5 text-tertiary" />
              </div>
              <div className="min-w-0">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-tertiary">1st Place • Top Performer</span>
                <p className="text-sm font-bold text-foreground truncate">{top3[0].name}</p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-extrabold text-foreground">
                {formatLeaderboardScore(top3[0].score, score_display_mode, top3[0].best_percentage, top3[0].best_percentile)}
              </p>
            </div>
          </div>
        ) : null}

        {/* Full Rankings List (Mobile Optimized) */}
        <div className="p-4 rounded-2xl border border-border/80 bg-card shadow-2xs space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider truncate">
              All Rankings ({selectedExam?.name})
            </h2>
            <span className="text-[10px] font-bold text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-md border border-border/60 shrink-0">
              {total} Aspirants
            </span>
          </div>

          <div className="divide-y divide-border/30">
            {entries.length === 0 ? (
              <div className="py-10 text-center text-xs text-muted-foreground space-y-2">
                <Trophy className="h-8 w-8 text-muted-foreground/30 mx-auto" />
                <p className="font-semibold text-foreground">No attempts recorded yet</p>
                <p className="text-[11px]">Be the first to complete a test for {selectedExam?.name}!</p>
              </div>
            ) : (
              entries.map((entry) => {
                const isMe = user_position?.rank === entry.rank
                return (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-2.5 py-2.5 transition-colors ${
                      isMe ? 'bg-primary/5 -mx-2 px-2 rounded-xl' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center w-6 shrink-0">
                      <RankIcon rank={entry.rank} />
                    </div>
                    <div
                      className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-extrabold shadow-2xs shrink-0 ${
                        entry.rank === 1
                          ? 'bg-tertiary/15 text-tertiary border border-tertiary/30'
                          : entry.rank === 2
                            ? 'bg-muted text-muted-foreground border border-border'
                            : entry.rank === 3
                              ? 'bg-secondary/15 text-secondary border border-secondary/30'
                              : 'bg-muted/60 text-muted-foreground'
                      }`}
                    >
                      <User className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold flex items-center gap-1.5 text-foreground">
                        <span className="truncate">{entry.name}</span>
                        {isMe && (
                          <span className="text-[9px] px-1.5 py-0.2 border border-primary/30 text-primary bg-primary/10 font-extrabold rounded-md shrink-0">
                            You
                          </span>
                        )}
                      </div>
                      {entry.tests_taken != null && (
                        <p className="text-[10px] text-muted-foreground">
                          {entry.tests_taken} test{entry.tests_taken === 1 ? '' : 's'}
                          {entry.best_percentile != null ? ` • Top ${(100 - entry.best_percentile).toFixed(0)}%` : ''}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-extrabold text-foreground">
                        {formatLeaderboardScore(entry.score, score_display_mode, entry.best_percentage, entry.best_percentile)}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* ─── DESKTOP VIEW ONLY (>= lg) ─── */}
      <div className="hidden lg:block space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-2.5 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Trophy className="h-3 w-3 text-primary" />
                  <span>Nationwide Rankings</span>
                </span>
                {selectedExam?.name && (
                  <Badge variant="outline" className="text-[10px] font-bold border-border/80 text-muted-foreground px-2 py-0.5 rounded-full">
                    {selectedExam.name}
                  </Badge>
                )}
              </div>

              <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
                Leaderboard & Standings
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Compare your performance, percentile ranks, and top scores with aspirants preparing for competitive exams.
              </p>
            </div>

            {/* Quick Metrics Strip — Vibrant Semantic Token Cards */}
            <div className="grid grid-cols-3 gap-2.5 sm:gap-3.5 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-primary/10 border border-primary/25 transition-all hover:bg-primary/15">
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Your Rank</span>
                <span className="text-xl sm:text-2xl font-extrabold text-primary mt-1">
                  {user_position ? `#${user_position.rank}` : '—'}
                </span>
              </div>

              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-secondary/10 border border-secondary/25 transition-all hover:bg-secondary/15">
                <span className="text-[10px] font-bold text-secondary uppercase tracking-wider">Best Score</span>
                <span className="text-xl sm:text-2xl font-extrabold text-secondary mt-1">
                  {user_position ? `${user_position.score} pts` : '—'}
                </span>
              </div>

              <div className="flex flex-col justify-center p-3.5 sm:p-4 rounded-xl bg-tertiary/10 border border-tertiary/25 transition-all hover:bg-tertiary/15">
                <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">Aspirants</span>
                <span className="text-xl sm:text-2xl font-extrabold text-tertiary mt-1">{total}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Exam Selection Pills */}
        {exams.length > 1 && (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5 p-1 bg-muted/50 rounded-2xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs max-w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
              {exams.map(e => (
                <button
                  key={e.id}
                  onClick={() => handleExamChange(e.id)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 ${
                    e.id === selectedExam?.id
                      ? 'bg-card text-foreground shadow-xs border border-border/40'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <span>{e.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* User Rank Card */}
        {user_position ? (
          <Card className="border border-primary/30 bg-primary/5 rounded-2xl shadow-glass-sm overflow-hidden">
            <CardContent className="p-4 sm:p-5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-primary text-primary-fg flex items-center justify-center font-extrabold text-sm shadow-xs shrink-0">
                  #{user_position.rank}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-primary truncate">
                    Your Standing — {selectedExam?.name}
                  </p>
                  <div className="text-sm sm:text-base font-extrabold text-foreground truncate">
                    Ranked #{user_position.rank}{' '}
                    <span className="text-xs font-normal text-muted-foreground">among {user_position.total} students</span>
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">{scoreMetricLabel}</p>
                <p className="text-lg sm:text-xl font-extrabold text-primary tracking-tight">
                  {formatLeaderboardScore(user_position.score, score_display_mode, user_position.best_percentage, user_position.best_percentile)}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-dashed border-border/80 rounded-2xl bg-card/60 shadow-glass-xs">
            <CardContent className="p-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2 font-medium">
              <TrendingUp className="h-4 w-4 text-primary shrink-0" />
              <span>
                Complete your first test in <strong className="text-foreground font-bold">{selectedExam?.name}</strong> to appear on the leaderboard!
              </span>
            </CardContent>
          </Card>
        )}

        {/* Top 3 Podium Spotlight Cards */}
        {top3.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 sm:gap-4">
            {top3.map((e, idx) => {
              const titles = ['1st Place', '2nd Place', '3rd Place']
              const borders = [
                'border-tertiary/40 bg-tertiary/5',
                'border-border/80 bg-card',
                'border-secondary/40 bg-secondary/5'
              ]

              return (
                <Card
                  key={e.user_id}
                  className={`p-4 sm:p-5 border rounded-2xl shadow-glass-sm flex flex-col justify-between space-y-3 ${
                    borders[idx] || 'border-border bg-card'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Badge
                      variant="outline"
                      className="text-[10px] font-extrabold uppercase border-border/60 text-muted-foreground rounded-lg"
                    >
                      {titles[idx] || `#${e.rank}`}
                    </Badge>
                    <RankIcon rank={e.rank} />
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-foreground truncate">{e.name}</h4>
                    <p className="text-xl font-extrabold text-foreground mt-0.5 tracking-tight">
                      {formatLeaderboardScore(e.score, score_display_mode, e.best_percentage, e.best_percentile)}
                    </p>
                  </div>
                </Card>
              )
            })}
          </div>
        )}

        {/* Full Rankings Table / List */}
        <Card className="rounded-2xl border border-border/80 bg-card shadow-glass-sm overflow-hidden">
          <CardHeader className="border-b border-border/40 py-3.5 px-4 sm:px-5 bg-muted/20">
            <CardTitle className="text-xs sm:text-sm font-extrabold flex items-center justify-between">
              <span className="text-foreground truncate">All Rankings ({selectedExam?.name})</span>
              <Badge variant="outline" className="font-bold text-[10px] sm:text-[11px] border-border/80 shrink-0 ml-2">
                {total} Participants
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-border/40">
            {entries.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground space-y-2">
                <Trophy className="h-8 w-8 text-muted-foreground/30 mx-auto" />
                <p className="font-semibold text-foreground">No attempts recorded yet</p>
                <p className="text-[11px]">Be the first to complete a test for {selectedExam?.name}!</p>
              </div>
            ) : (
              entries.map((entry) => {
                const isMe = user_position?.rank === entry.rank
                return (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-3 p-3.5 sm:p-4 transition-colors ${
                      isMe ? 'bg-primary/10 font-medium' : 'hover:bg-muted/20'
                    }`}
                  >
                    <div className="flex items-center justify-center w-7 shrink-0">
                      <RankIcon rank={entry.rank} />
                    </div>
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-extrabold shadow-xs shrink-0 ${
                        entry.rank === 1
                          ? 'bg-tertiary/15 text-tertiary border border-tertiary/30'
                          : entry.rank === 2
                            ? 'bg-muted text-muted-foreground border border-border'
                            : entry.rank === 3
                              ? 'bg-secondary/15 text-secondary border border-secondary/30'
                              : 'bg-muted/60 text-muted-foreground'
                      }`}
                    >
                      <User className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-extrabold flex items-center gap-2 text-foreground">
                        <span className="truncate">{entry.name}</span>
                        {isMe && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1.5 py-0 border-primary/40 text-primary bg-primary/10 font-extrabold rounded-md shrink-0"
                          >
                            You
                          </Badge>
                        )}
                      </div>
                      {entry.tests_taken != null && (
                        <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium">
                          {entry.tests_taken} test{entry.tests_taken === 1 ? '' : 's'} completed
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm sm:text-base font-extrabold text-foreground tracking-tight">
                        {formatLeaderboardScore(entry.score, score_display_mode, entry.best_percentage, entry.best_percentile)}
                      </p>
                      {entry.best_percentile != null && score_display_mode !== 'percentile' && score_display_mode !== 'nta_score' && (
                        <p className="text-[10px] text-muted-foreground font-semibold">
                          Top {(100 - entry.best_percentile).toFixed(0)}%
                        </p>
                      )}
                      {entry.score != null && (score_display_mode === 'percentage' || score_display_mode === 'percentile' || score_display_mode === 'nta_score') && (
                        <p className="text-[10px] text-muted-foreground font-semibold">
                          Raw: {entry.score} pts
                        </p>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>
      </div>
    </StudentShell>
  )
}
